import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { PageHeader, Badge } from "@/components/Kit";
import SchoolForm from "./SchoolForm";
import PhonePeModal from "./PhonePeModal";
import { Link } from "react-router-dom";
import { Plus, Power, Trash2, CheckCircle2, Pencil, ExternalLink, Globe, Smartphone } from "lucide-react";

const STATUS_COLOR = { active: "#10B981", pending: "#F59E0B", suspended: "#F43F5E" };
const PLAN_COLOR = { basic: "#06B6D4", standard: "#4F46E5", premium: "#F59E0B" };
const planColor = (p) => PLAN_COLOR[p] || "#10B981";

export default function Schools() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ppFor, setPpFor] = useState(null);

  const { data: schools = [] } = useQuery({ queryKey: ["sa-schools"], queryFn: async () => (await api.get("/superadmin/schools")).data });

  const toggle = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/superadmin/schools/${id}`, { status })).data,
    onSuccess: () => { toast.success("School updated"); qc.invalidateQueries({ queryKey: ["sa-schools"] }); },
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/superadmin/schools/${id}`)).data,
    onSuccess: () => { toast.success("School removed"); qc.invalidateQueries({ queryKey: ["sa-schools"] }); },
  });

  return (
    <div data-testid="schools-page">
      <PageHeader
        title="School Management"
        subtitle={`${schools.length} schools on the platform`}
        action={<button className="ep-btn" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-school-btn"><Plus size={16} /> Onboard School</button>}
      />

      <div className="ep-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">School</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">City / Contact</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schools.map((s) => (
                <tr key={s.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`school-row-${s.code}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      {s.logo_url ? <img src={fileUrl(s.logo_url)} alt="" className="h-10 w-10 rounded-xl object-cover ring-1 ring-slate-700" data-testid={`school-logo-${s.code}`} /> : <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/20 text-xs font-bold text-indigo-300">{s.code.slice(0, 2)}</div>}
                      <div><p className="font-medium text-white">{s.name}</p><p className="text-[11px] text-slate-500">{s.email || "—"}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-slate-400">{s.code}</td>
                  <td className="px-4 py-2.5 text-slate-400">{s.city}<br /><span className="text-xs">{s.phone}</span></td>
                  <td className="px-4 py-2.5"><Badge color={planColor(s.plan)}>{s.plan}</Badge></td>
                  <td className="px-4 py-2.5"><Badge color={STATUS_COLOR[s.status]}>{s.status}</Badge></td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      <a href={`/s/${s.code}`} target="_blank" rel="noreferrer" className="rounded-lg bg-slate-700/40 p-2 text-slate-300 hover:bg-slate-700/70" title="Public portal" data-testid={`portal-${s.code}`}><ExternalLink size={15} /></a>
                      <Link to={`/super/schools/${s.id}/website`} className="rounded-lg bg-cyan-500/15 p-2 text-cyan-300 hover:bg-cyan-500/25" title="Edit public website" data-testid={`website-${s.code}`}><Globe size={15} /></Link>
                      <button onClick={() => setPpFor(s)} className="rounded-lg bg-[#5F259F]/30 p-2 text-[#c9a6ff] hover:bg-[#5F259F]/50" title="PhonePe gateway" data-testid={`phonepe-${s.code}`}><Smartphone size={15} /></button>
                      <button onClick={() => { setEditing(s); setOpen(true); }} className="rounded-lg bg-indigo-500/15 p-2 text-indigo-300 hover:bg-indigo-500/25" title="Edit" data-testid={`edit-${s.code}`}><Pencil size={15} /></button>
                      {s.status === "pending" && (
                        <button onClick={() => toggle.mutate({ id: s.id, status: "active" })} className="rounded-lg bg-emerald-500/15 p-2 text-emerald-400 hover:bg-emerald-500/25" title="Approve" data-testid={`approve-${s.code}`}><CheckCircle2 size={15} /></button>
                      )}
                      <button onClick={() => toggle.mutate({ id: s.id, status: s.status === "suspended" ? "active" : "suspended" })} className="rounded-lg bg-amber-500/15 p-2 text-amber-400 hover:bg-amber-500/25" title="Suspend/Activate" data-testid={`toggle-${s.code}`}><Power size={15} /></button>
                      <button onClick={() => remove.mutate(s.id)} className="rounded-lg bg-rose-500/15 p-2 text-rose-400 hover:bg-rose-500/25" title="Delete" data-testid={`delete-${s.code}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SchoolForm open={open} onClose={() => setOpen(false)} editing={editing} />
      <PhonePeModal school={ppFor} onClose={() => setPpFor(null)} />
    </div>
  );
}
